import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SEO from '../components/SEO';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '40vh',
  minHeight: '300px',
  color: 'white',
  textAlign: 'center',
  overflow: 'hidden',
  background: 'linear-gradient(45deg, #3c005a 30%, #5c0080 90%)',
  padding: theme.spacing(8, 0),
}));

const BannerImage = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transition: 'opacity 1s ease-in-out',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
}));

const FAQ = () => {
  const [currentBanner, setCurrentBanner] = useState(0);

  const banners = [
    '/banner1.jpg',
    '/banner2.jpg',
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const faqs = [
    {
      question: 'How does a drug safety database streamline data management?',
      answer: 'A drug safety database software effectively centralizes and organizes vast amounts of safety-related data, including adverse event reports, medication errors, and product complaints. By providing a single, comprehensive platform for data storage and management, it reduces the complexities associated with manual data handling and improves efficiency in pharmacovigilance operations.',
    },
    {
      question: 'How can a drug safety database facilitate and ensure regulatory compliance?',
      answer: 'A drug safety database software facilitates compliance with regulatory requirements by enabling timely reporting and documentation of safety-related information. It automates the generation of regulatory reports, such as periodic safety update reports (PSURs) and expedited safety communications, ensuring adherence to regulatory standards and guidelines. Additionally, it maintains comprehensive audit trails of all data modifications and user activities, supporting transparency and traceability in pharmacovigilance processes.',
    },
    {
      question: 'What are the customizations and flexibilities expected in a drug safety database?',
      answer: 'A drug safety database should offer several key customizations and flexibilities:\n\n' +
        '1. Flexible Data Schema: The database should allow for the customization of data fields and schema to align with the unique requirements of different pharmaceutical companies and regulatory bodies.\n\n' +
        '2. Configurable Workflows: The database should offer configurable workflows that allow users to define and automate various pharmacovigilance processes.\n\n' +
        '3. Scalability: As pharmacovigilance databases accumulate large volumes of safety-related data over time, scalability is essential to support growing data storage and processing requirements.\n\n' +
        '4. Integration Capabilities: The database should support seamless integration with external systems and data sources, such as electronic health records (EHRs), clinical trial databases, and spontaneous reporting systems.',
    },
    {
      question: 'What kind of support and training do you provide?',
      answer: 'We provide comprehensive support and training to ensure successful implementation and usage of our software. This includes:\n\n' +
        '1. Initial setup and configuration assistance\n' +
        '2. User training sessions\n' +
        '3. Technical documentation\n' +
        '4. 24/7 technical support\n' +
        '5. Regular updates and maintenance',
    },
    {
      question: 'How secure is the data stored in your system?',
      answer: 'We take data security very seriously. Our system implements multiple layers of security measures:\n\n' +
        '1. End-to-end encryption\n' +
        '2. Role-based access control\n' +
        '3. Regular security audits\n' +
        '4. Compliance with international data protection standards\n' +
        '5. Secure data backup and recovery systems',
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    publisher: {
      '@type': 'Organization',
      name: 'Thranatech.com',
      url: 'https://www.thranatech.com',
    },
    mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
    })),
  };

  return (
    <>
      <SEO
        title="Thrana FAQ | Pharmacovigilance Platform & Training Answers"
        description="Find answers to common questions about Thrana's pharmacovigilance software, services, and training programs."
        path="/faq"
        structuredData={structuredData}
      />
      <Box>
      <HeroSection>
        {banners.map((banner, index) => (
          <BannerImage
            key={index}
            sx={{
              backgroundImage: `url(${banner})`,
              opacity: currentBanner === index ? 1 : 0,
            }}
          />
        ))}
        <Overlay>
          <Container maxWidth="md">
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                fontWeight: 'bold',
                color: 'white',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="h6"
              paragraph
              sx={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                color: 'white',
              }}
            >
              Find answers to common questions about our services
            </Typography>
          </Container>
        </Overlay>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8 }}>
        {faqs.map((faq, index) => (
          <Accordion key={index} sx={{ mb: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography variant="h6">{faq.question}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-line' }}
              >
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Container>
      </Box>
    </>
  );
};

export default FAQ; 